/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { KgDoc, KgEdge, KgNode, edgesOf } from './types';

// Pure derivations for the Level-1 (symbol-altitude) rendering. Graphify's
// node-link output has no explicit kind field on most nodes, so kind and
// structure are derived: `contains` edges define file/module containers, and
// the callable flags split the leaf symbols.

export type SymbolKind = 'class' | 'function' | 'concept' | 'symbol';

export const symbolKind = (n: KgNode): SymbolKind => {
  if (n._callable_class) return 'class';
  if (n._callable) return 'function';
  if (n.file_type === 'concept') return 'concept';
  return 'symbol';
};

// Relation vocabulary observed in Graphify artifacts, folded into legend
// families so the legend stays short as the extractor grows synonyms.
export type RelationFamily =
  | 'contains'
  | 'references'
  | 'imports'
  | 'calls'
  | 'inherits'
  | 'defines'
  | 'other';

export const relationFamily = (relation?: string): RelationFamily => {
  switch (relation) {
    case 'contains':
      return 'contains';
    case 'references':
      return 'references';
    case 'imports':
    case 'imports_from':
      return 'imports';
    case 'calls':
    case 'method':
      return 'calls';
    case 'inherits':
    case 'extends':
      return 'inherits';
    case 'defines':
      return 'defines';
    default:
      return 'other';
  }
};

// Node sizing by degree renders as font buckets, not free scaling — the
// measure pass stays truthful and the step is visible at a glance.
export const degreeBucket = (degree: number): 0 | 1 | 2 | 3 => {
  if (degree >= 7) return 3;
  if (degree >= 4) return 2;
  if (degree >= 2) return 1;
  return 0;
};

export type RepoGroup = {
  container: KgNode;
  members: KgNode[];
};

export type RepoGraphModel = {
  groups: RepoGroup[];
  loose: KgNode[];
  // Everything that draws as a line: semantic edges plus container→container
  // containment (member containment is absorbed by the grouping itself).
  drawnEdges: KgEdge[];
  degree: Map<string, number>;
  familyCounts: Map<RelationFamily, number>;
};

export const buildRepoGraphModel = (doc: KgDoc): RepoGraphModel => {
  const nodes = doc.nodes;
  const byId = new Map(nodes.map(n => [n.id, n]));
  const all = edgesOf(doc).filter(
    e => byId.has(String(e.source)) && byId.has(String(e.target)),
  );

  const containment = all.filter(e => relationFamily(e.relation) === 'contains');
  const containerIds = new Set(containment.map(e => String(e.source)));

  // One level of nesting only: a container contained by another container
  // stays top-level and that containment draws as an edge instead. First
  // parent wins on (unobserved so far) multi-parent inputs.
  const parentOf = new Map<string, string>();
  const drawnEdges: KgEdge[] = [];
  for (const e of containment) {
    const target = String(e.target);
    if (containerIds.has(target)) {
      drawnEdges.push(e);
    } else if (!parentOf.has(target)) {
      parentOf.set(target, String(e.source));
    }
  }
  for (const e of all) {
    if (relationFamily(e.relation) !== 'contains') drawnEdges.push(e);
  }

  const membersOf = new Map<string, KgNode[]>();
  for (const [child, parent] of parentOf) {
    const list = membersOf.get(parent) ?? [];
    list.push(byId.get(child)!);
    membersOf.set(parent, list);
  }

  const groups: RepoGroup[] = [];
  const loose: KgNode[] = [];
  for (const n of nodes) {
    if (parentOf.has(n.id)) continue;
    const members = membersOf.get(n.id);
    // A container whose only children are other containers has no members
    // left after flattening; a group box with nothing inside is noise.
    if (members?.length) {
      groups.push({ container: n, members });
    } else {
      loose.push(n);
    }
  }

  const degree = new Map<string, number>();
  const familyCounts = new Map<RelationFamily, number>();
  for (const e of drawnEdges) {
    const s = String(e.source);
    const t = String(e.target);
    degree.set(s, (degree.get(s) ?? 0) + 1);
    degree.set(t, (degree.get(t) ?? 0) + 1);
    const fam = relationFamily(e.relation);
    familyCounts.set(fam, (familyCounts.get(fam) ?? 0) + 1);
  }

  return { groups, loose, drawnEdges, degree, familyCounts };
};

// Search: substring match over the human-facing fields. Empty query matches
// everything (the renderer treats that as "no filter active").
export const nodeMatches = (n: KgNode, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [n.label, n.id, n.source_file]
    .filter(Boolean)
    .some(v => String(v).toLowerCase().includes(q));
};

// ELK edge ids: built by RepoGraph when submitting the layout and looked up
// by RepoGraphCanvas when painting routes — one definition, or a drift makes
// every edge silently vanish (routes.get misses return undefined).
export const edgeId = (
  e: { source: unknown; target: unknown },
  i: number,
): string => `e${i}-${e.source}->${e.target}`;
